import { Box, Button, Typography, Skeleton, Divider } from "@mui/joy";
import React, { useContext, useEffect, useState, useRef } from "react";
import AutoBuildFormSection from "../../components/AutomaticFormBuilder";
import { AlertContext } from "../../components/AlertContext";
import NavBlocker from "../../components/NavBlocker";

import { PersonOutlined } from "@mui/icons-material";
import "../../styling/CurrentVisit.css";
import FeedIcon from '@mui/icons-material/Feed';
import { clearLocalVisitId, getLocalVisitId, setLocalVisitId } from "../../utils/LocalVisit";
import { getVisitById, createVisit, putVisit } from "../../services/Visit";
import { mapSectionFieldsAndInitialInputs } from "../../utils/SetupInitialInputs";
import qs from 'qs';
import { dateToISO, isoToDate } from "../../helpers/TimeConversionHelper";
import { useNavigate } from "react-router-dom";

export const visitFormFields = {
    "field_order": ["date", "efrt_activation_time", "ambulance_number", "transport"],
    "date": {
        "type": "datetime",
        "display": "datetime_date",
        "required": true,
    },
    "efrt_activation_time": {
        "type": "datetime",
        "display": "datetime_hour",
        "required": false,
    },
    "ambulance_number": {
        "type": "string",
        "required": false,
    },
    "transport": {
        "type": "string",
        "display": "dropdown",
        "values": ["Code 1 (Non-Transport)", "Code 1 (Immediate OMA)", "Code 1 (Security)", "Code 2 (McMaster Children's Hospital)", "Code 2 (Security to McMaster Children's Hospital)", "Code 3 (Ambulance)", "Other", "None"],
        "required": true,
    }
}

export const patientFormFields = {
    "field_order": ["first_name", "last_name", "sex", "date_of_birth", "address", "id_number"],
    "first_name": {
        "type": "string",
        "required": true,
    },
    "last_name": {
        "type": "string",
        "required": true,
    },
    "sex": {
        "type": "toggle",
        "values": ["male", "female", "other"],
        "required": true,
    },
    "date_of_birth": {
        "type": "datetime",
        "display": "datetime_date",
        "required": false,
    },
    "address": {
        "type": "string",
        "required": false,
    },
    "id_number": {
        "type": "string",
        "required": false,
    },
};

// This page is only going to be used to create forms. We can still save and shit from this form 
// But only for the form_top elements
function NewVisit() {
    const { addAlert } = useContext(AlertContext);
    const [ ready, setReady ] = useState(false);
    const [ resetFormKey, setResetFormKey ] = useState(0);
    const [ dirty, setDirty ] = useState(false);
    const navigate = useNavigate();
    const initialInputs = useRef({});
    const formRef = useRef();

    // Check existing visit
    const checkExistingVisit = async () => {
        const localVisitId = getLocalVisitId();
        if (!localVisitId) {
            setReady(true);
            return;
        }
        // Grab said visit -> This check will also take care of auth errors
        // If there is no visit, that's fine. Just takeout that Id
        try {
            const visit = await getVisitById(localVisitId);
            if (visit.status !== "draft") {
                addAlert("Currently saved visit is not in draft anymore. Clearing!", "danger");
                clearLocalVisitId();
                setReady(true);
                return;
            }
            if (visit) {
                // Adjust datetimes to be acceptable format
                visit['details']['date'] = visit['details']['incident_occurred_at'];
                if (visit['patient_info']['date_of_birth']) {
                    visit['patient_info']['date_of_birth'] = isoToDate(visit['patient_info']['date_of_birth']);
                }
                visit['details']['efrt_activation_time'] = visit['details']['efrt_activated_at'];

                initialInputs.current['visitFormFields'] = mapSectionFieldsAndInitialInputs(visitFormFields, visit['details'])
                initialInputs.current['patientFormFields'] = mapSectionFieldsAndInitialInputs(patientFormFields, visit['patient_info']);
            } else {
                clearLocalVisitId();
            }
            setReady(true);
        } catch (error) {
            addAlert("Couldn't check for local visit", "danger");
            console.log(error);
        }
    }
    
    useEffect(() => {
        checkExistingVisit();
    }, [])

    const handleSubmit = async (event) => {
        event.preventDefault();

        const localVisitId = getLocalVisitId();
        if (localVisitId && !dirty) {
            navigate(`/new-report/${localVisitId}`);
            addAlert("Continuing!", "success");
            return;
        }
        const formData = new FormData(event.target);
        const values = Object.fromEntries(formData.entries());
        const nested = qs.parse(values);
        // ONLY FOR SUBMITTING POST VISIT -> CHANGE DATES AND TIMES TO DATETIMES
        if ('date_of_birth' in nested.patient_info && typeof nested.patient_info['date_of_birth'] === "string" && nested.patient_info['date_of_birth'].length > 0) {
            nested.patient_info['date_of_birth'] = dateToISO(nested.patient_info['date_of_birth']);
        } else {
            delete nested.patient_info['date_of_birth']
        }

        if (localVisitId && dirty) {
            try {
                const visit = await putVisit({
                    visitId: localVisitId, 
                    patientInfo: nested.patient_info,
                    details: nested.details,
                });
                setDirty(false);
                setLocalVisitId(visit.id);
                setTimeout(() => {
                    navigate(`/new-report/${visit.id}`);
                }, 0)
            } catch(error) {
                console.log(error)
            }
            return;
        }
        try {
            const visit = await createVisit(nested.patient_info, nested.details);
            addAlert("Report initialized!", "success");
            setLocalVisitId(visit.id);
            setDirty(false);
            setTimeout(() => {
                navigate(`/new-report/${visit.id}`);
            }, 0)
            return;
        } catch (error) {
            console.log(error);
            addAlert("Couldn't create form", "danger")
        }
    }



    const handleReset = () => {
        try {
            formRef.current.reset();
            initialInputs.current = {};
            setResetFormKey((reset) => reset + 1);
            clearLocalVisitId();
            addAlert("Reset form", "success");
        } catch(error) {
            addAlert("Form could not be reset", "danger")
        }
    }

    const handleOnInput = () => {
        setDirty(true);
    }

    return <Box>
        <NavBlocker dirty={dirty}/>
        {/* <img src={organizationFormFields["settings"]["logo_url"]} alt="Organization Logo" style={{height: "10vh", minHeight: "85px", padding: "15px"}}/> */}
        <Divider sx={{width: "100%"}}/>
        <Box sx={{ px: 4 }}>
            {ready ? <form onSubmit={handleSubmit} onReset={handleReset} ref={formRef} key={resetFormKey} onInput={handleOnInput} autoComplete="off">
                <Typography sx={{py: 1}}> 
                    <Typography level="h3">
                        Instructions: &nbsp;
                    </Typography>
                    Please fill out the patient's information for the report first to enable dictation and cloud saving!
                </Typography>
                <Box>
                    {/* Date, EMS Activation, Ambulance #, Transport*/}
                    <Box sx={{p: 2, border: "1px #c5cfd3 solid", borderRadius: "10px", overflow: 'auto'}} key={'visit_form'} id='visit_details'>
                        <Typography level='h3' startDecorator={<FeedIcon fontSize="xl4"/>}> Report Information</Typography>
                        <Divider sx={{my: 1}}/>
                        <AutoBuildFormSection formFields={visitFormFields} initialInput={initialInputs.current['visitFormFields']} section='details'/>
                    </Box>
                    <Divider sx={{my: 3}}/>
                    <Box sx={{p: 2, border: "1px #c5cfd3 solid", borderRadius: "10px", overflow: 'auto'}} key={'patient_form'} id='patient_form'>
                        <Typography level='h3' startDecorator={<PersonOutlined fontSize="xl4"/>}> Patient Information</Typography>
                        <Divider sx={{my: 1}}/>
                        <AutoBuildFormSection formFields={patientFormFields} initialInput={initialInputs.current['patientFormFields']} section='patient_info'/>
                    </Box>
                </Box>
                <Box sx={{
                    my: 2,
                    display: "flex",
                    gap: 5
                }}>
                    <Button 
                    type="reset"
                    sx={{
                        display: "flex", 
                        width: "25%"
                    }}
                    >
                        Clear
                    </Button>
                    <Button 
                        type="submit"
                        sx={{
                            display: "flex",
                            width: "75%",
                            color: "white",
                            backgroundColor: "var(--dark-blue-button)",
                            '&:hover': {
                                backgroundColor: "var(--dark-blue-button-hover)",
                            },
                        }}>
                        Next
                    </Button>
                </Box>
            </form> : <Skeleton />}
        </Box>
    </Box>
}

export default NewVisit;